/**
 * Get the timezone abbreviation for a given IANA timezone string
 * @param {string} timezone - IANA timezone string (e.g., 'America/Chicago')
 * @param {Date} date - Date to get abbreviation for (defaults to now)
 * @returns {string} Timezone abbreviation (e.g., 'CST', 'CDT')
 */
export function getTimezoneAbbreviation(timezone, date = new Date()) {
    if (!timezone) return '';
    
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short'
        });
        
        const parts = formatter.formatToParts(date);
        const timeZoneName = parts.find(p => p.type === 'timeZoneName')?.value;
        return timeZoneName || '';
    } catch (error) {
        console.error('Error getting timezone abbreviation:', error);
        return '';
    }
}

/**
 * Get the user's device timezone
 * @returns {string} IANA timezone string (e.g., 'America/Chicago')
 */
export function getDeviceTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
        console.error('Error getting device timezone:', error);
        return 'UTC';
    }
}

/**
 * Format time with timezone abbreviation if it differs from device timezone
 * @param {Date} date - Date to format
 * @param {string} orgTimezone - Organization's timezone
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted time with optional timezone abbreviation
 */
export function safeNewDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
}

export function formatTimeWithTimezone(date, orgTimezone, options = {}) {
    if (!date || isNaN(date.getTime())) return '';
    
    const deviceTimezone = getDeviceTimezone();
    const timezonesDiffer = orgTimezone && deviceTimezone !== orgTimezone;
    
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: orgTimezone || deviceTimezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...options
    });
    
    let formattedTime = formatter.format(date);
    
    if (timezonesDiffer && orgTimezone) {
        const abbreviation = getTimezoneAbbreviation(orgTimezone, date);
        if (abbreviation) {
            formattedTime += ` (${abbreviation})`;
        }
    }
    
    return formattedTime;
}
